import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import AdminShell from "@/components/AdminShell";
import { Eyebrow } from "@/components/brand";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function FlaggedMessagesPage() {
  const [flaggedConversations, setFlaggedConversations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [respondingTo, setRespondingTo] = useState(null);
  const [response, setResponse] = useState("");
  const [calendlyLink, setCalendlyLink] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const navigate = useNavigate();
  const token = localStorage.getItem("token");

  const fetchFlaggedMessages = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/chat/flagged-messages`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setFlaggedConversations(response.data.flagged_conversations);
    } catch (error) {
      toast.error("Failed to load flagged messages");
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => { fetchFlaggedMessages(); }, [fetchFlaggedMessages]);

  const handleRespond = async (conversationId, messageIndex) => {
    if (!response.trim()) {
      toast.error("Please enter a response");
      return;
    }

    setSubmitting(true);
    try {
      await axios.post(`${API}/chat/respond-to-flag`, {
        conversation_id: conversationId,
        message_index: messageIndex,
        response: response,
        calendly_link: calendlyLink || null
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      toast.success("Response sent! Client has been notified via email.");
      setResponse("");
      setCalendlyLink("");
      setRespondingTo(null);
      fetchFlaggedMessages(); // Refresh list
    } catch (error) {
      toast.error("Failed to send response");
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <AdminShell active="flagged">
        <div className="flex items-center justify-center py-24">
          <p className="text-mute text-sm">Loading flagged messages...</p>
        </div>
      </AdminShell>
    );
  }

  return (
    <AdminShell active="flagged">
      {/* Page header */}
      <div className="mb-10 flex flex-wrap items-end justify-between gap-4">
        <div>
          <Eyebrow className="mb-3">Human assistance</Eyebrow>
          <h1 className="text-4xl sm:text-5xl font-extrabold text-ink tracking-tight leading-[1.05]">Flagged Messages</h1>
          <p className="text-mute mt-4">
            Messages that need human assistance
          </p>
        </div>
        <Button onClick={() => navigate("/admin")} variant="outline" className="h-10">
          Back to Dashboard
        </Button>
      </div>

      {/* Content */}
      <div className="max-w-5xl">
        {flaggedConversations.length === 0 ? (
          <div className="card-flat py-14 text-center">
            <div className="w-14 h-14 bg-mint border border-[#bfe5d1] rounded-xl flex items-center justify-center mx-auto mb-4">
              <svg className="w-7 h-7 text-equal-dark" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
            </div>
            <h3 className="text-lg font-bold text-ink tracking-tight mb-1">No Flagged Messages</h3>
            <p className="text-mute text-sm">All messages have been addressed. Great work!</p>
          </div>
        ) : (
          <div className="space-y-6">
            {flaggedConversations.map((conv) => (
              <section key={conv.conversation_id} className="card-flat">
                <div className="px-6 py-5 border-b border-line flex items-center justify-between gap-4">
                  <div>
                    <h2 className="text-base font-bold text-ink tracking-tight">
                      Conversation with {conv.user_email}
                    </h2>
                    <p className="text-xs text-mute mt-1 font-mono">
                      Started: {new Date(conv.created_at).toLocaleString()}
                    </p>
                  </div>
                  <span className="px-3 py-1 bg-warm text-[#c47a12] border border-[#f3d9b0] text-xs font-semibold rounded-md whitespace-nowrap">
                    {conv.flagged_messages.length} Flagged
                  </span>
                </div>
                <div className="p-6">
                  <div className="space-y-4">
                    {conv.flagged_messages.map((msg) => (
                      <div key={msg.message_index} className="border border-line rounded-xl p-4">
                        {/* Flagged Message */}
                        <div className="bg-[#fdecea] border-l-4 border-[#c0392b] p-4 rounded-[8px]">
                          <div className="flex items-start justify-between mb-2">
                            <p className="th-mono text-[#c0392b]">
                              🚩 FLAGGED MESSAGE
                            </p>
                            <p className="text-xs text-mute font-mono">
                              {new Date(msg.flagged_at).toLocaleString()}
                            </p>
                          </div>
                          <p className="text-sm text-ink mb-2 leading-relaxed">{msg.content}</p>
                          <p className="text-xs text-[#c0392b]">
                            <strong>Reason:</strong> {msg.flag_reason}
                          </p>
                        </div>

                        {/* Admin Response Section */}
                        {msg.admin_response ? (
                          <div className="mt-4 bg-mint border-l-4 border-equal p-4 rounded-[8px]">
                            <div className="flex items-start justify-between mb-2">
                              <p className="th-mono text-equal-dark">
                                ✅ YOUR RESPONSE
                              </p>
                              <p className="text-xs text-mute font-mono">
                                {new Date(msg.admin_response_at).toLocaleString()}
                              </p>
                            </div>
                            <p className="text-sm text-ink whitespace-pre-wrap leading-relaxed">{msg.admin_response}</p>
                            {msg.client_notified && (
                              <p className="text-xs text-equal-dark mt-2">
                                ✓ Client notified via email
                              </p>
                            )}
                          </div>
                        ) : respondingTo === `${conv.conversation_id}-${msg.message_index}` ? (
                          <div className="mt-4 space-y-3">
                            <div>
                              <label className="block text-sm font-semibold text-ink mb-2">
                                Your Response to Client
                              </label>
                              <Textarea
                                value={response}
                                onChange={(e) => setResponse(e.target.value)}
                                placeholder="Type your response here..."
                                rows={4}
                                className="w-full"
                              />
                            </div>
                            <div>
                              <label className="block text-sm font-semibold text-ink mb-2">
                                Calendly Link (Optional)
                              </label>
                              <Input
                                type="url"
                                value={calendlyLink}
                                onChange={(e) => setCalendlyLink(e.target.value)}
                                placeholder="https://calendly.com/your-link"
                                className="w-full"
                              />
                              <p className="text-xs text-mute mt-1">
                                Include your Calendly link so the client can schedule a call
                              </p>
                            </div>
                            <div className="flex gap-2">
                              <Button
                                onClick={() => handleRespond(conv.conversation_id, msg.message_index)}
                                disabled={submitting}
                              >
                                {submitting ? "Sending..." : "Send Response & Notify Client"}
                              </Button>
                              <Button
                                onClick={() => {
                                  setRespondingTo(null);
                                  setResponse("");
                                  setCalendlyLink("");
                                }}
                                variant="outline"
                                disabled={submitting}
                              >
                                Cancel
                              </Button>
                            </div>
                          </div>
                        ) : (
                          <div className="mt-4">
                            <Button
                              onClick={() => setRespondingTo(`${conv.conversation_id}-${msg.message_index}`)}
                            >
                              Respond to Client
                            </Button>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              </section>
            ))}
          </div>
        )}
      </div>
    </AdminShell>
  );
}
